/**
 * quick-links.js
 */

import React from 'react';
import { Box } from '@mui/material';
import { Link } from 'react-router-dom';
import { LATE_COMPLETION_DAYS, Types } from 'utils/constants';
import { addDays } from 'date-fns';
import { getDatesFromRangeType, formatDateParams } from 'utils/datetime';
import { UserContext } from 'contexts/user/user-context';
import RouteLink from 'routing/route-link';
import useRecordStore from 'contexts/record-store';
import { DogsContext } from 'contexts/dogs/dogs-context';
import Popover from 'components/popover';
import CardLink from 'components/card-link';


export default function QuickLinks(props) {
  const {
    label,
    activeRecordFilterId,
    ...rest
  } = props;
  const { user } = React.useContext(UserContext);
  const { getActiveDogs } = React.useContext(DogsContext);
  const recordStore = useRecordStore();
  const status = recordStore.useStatus();
  const incompleteCount = status.stats.incompleteCount.get();
  const notReviewedCount = status.stats.notReviewedCount.get();
  const lateCompletionCount = status.stats.lateCompletionCount.get();
  const rejectedCount = status.stats.rejectedCount.get();
  const outdatedCount = status.stats.outdatedCount.get();
  const overdueVaccinationCount = status.stats.overdueVaccinationCount.get();
  const deploymentTrackMapCount = status.stats.deploymentTrackMapCount.get();

  const quickLinkRange = 'Last 90 Days';
  const { startDate, endDate } = getDatesFromRangeType(Types.DateRange.LAST_90_DAYS);
  const defaultDatePathParams = formatDateParams(startDate, endDate, Types.DateRange.LAST_90_DAYS);


  // quick links
  const quickLinkItems = React.useMemo(() => {
    const makePathForType = (id) => {
      switch (id) {
        case Types.QuickLinkType.INCOMPLETE:
          return `complete=0;${defaultDatePathParams}`;
        case Types.QuickLinkType.REJECTED:
          return `reviewStatus=${Types.ReviewStatus.REJECTED};${defaultDatePathParams}`;
        case Types.QuickLinkType.OUTDATED:
          return `recordGroups=[${Types.RecordGroup.TRAINING}];outdated=1;${defaultDatePathParams}`;
        case Types.QuickLinkType.LATE_COMPLETION:
          const datePathParams = formatDateParams(startDate, addDays(new Date(), -LATE_COMPLETION_DAYS), Types.DateRange.CUSTOM);
          return `complete=0;${datePathParams}`;
        case Types.QuickLinkType.NOT_REVIEWED:
          return `reviewStatus=${Types.ReviewStatus.NOT_REVIEWED};${defaultDatePathParams}`;
        case 'live-tracking-map':
          return `recordGroups=[${Types.RecordGroup.DEPLOYMENT}];workMode=${Types.WorkMode.PATROL};patrolType=${Types.Patrol.TRACKING};hasTrack=1`;
        default:
          return '';
      }
    };

    const items = [];
    const addItem = (count, icon, title, QuickLinkType, tooltip) => {
      if (count > 0) {
        const itemBase = {
          id: QuickLinkType,
          icon,
          count,
          title,
          tooltip,
        }

        if (QuickLinkType === Types.QuickLinkType.OVERDUE_VAC) {
          const activeDogs = getActiveDogs();
          items.push({
            ...itemBase,
            component: Link,
            componentParams: {
              to: user.role.isSupervisor 
                ? '/vaccines' 
                : activeDogs.length === 1
                  ? `/dogs/${activeDogs[0].id}/edit/vaccinations`
                  : '/dogs'
            }
          })
        } else {
          const pathParams = {
            p1: Types.FilterMode.QUICK_LINK,
            p2: QuickLinkType,
            filterParams: makePathForType(QuickLinkType)
          };
          items.push({
            ...itemBase,
            secondaryLabel: quickLinkRange,
            component: RouteLink,
            componentParams: {
              pathSectionName: 'records',
              pathItemName: 'params',
              pathParams,
            }
          });          
        }
      }
    }

    // HANDLER
    if (user.role.isHandler) {
      addItem(incompleteCount, 'incomplete', `Incomplete Record${incompleteCount === 1 ? '' : 's'}`, Types.QuickLinkType.INCOMPLETE);
      addItem(rejectedCount, 'rejected', `Rejected Record${rejectedCount === 1 ? '' : 's'}`, Types.QuickLinkType.REJECTED);
      addItem(outdatedCount, 'outdated', `Outdated Record${outdatedCount === 1 ? '' : 's'}`, Types.QuickLinkType.OUTDATED);
      addItem(overdueVaccinationCount, 'vaccination', `Overdue Vaccination${overdueVaccinationCount === 1 ? '' : 's'}`, Types.QuickLinkType.OVERDUE_VAC);
    }
    // SUPERVISOR
    else if (user.role.isSupervisor) {
      addItem(
        lateCompletionCount,
        'late-completion',
        `Late Record${lateCompletionCount === 1 ? '' : 's'}`,
        Types.QuickLinkType.LATE_COMPLETION,
        <>A record is late if it's incomplete<br />and more than 2 weeks old</>);
      addItem(
        notReviewedCount,
        'not-reviewed',
        `Record${notReviewedCount === 1 ? '' : 's'} Not Reviewed`,
        Types.QuickLinkType.NOT_REVIEWED);
      addItem(
        deploymentTrackMapCount,
        'map',
        `Recent Track${deploymentTrackMapCount === 1 ? '' : 's'}`,
        'live-tracking-map');
      addItem(overdueVaccinationCount, 'vaccination', `Overdue Vaccination${overdueVaccinationCount === 1 ? '' : 's'}`, Types.QuickLinkType.OVERDUE_VAC);
    }
    // TRAINER
    else if (user.role.isTrainer) {
      addItem(
        lateCompletionCount,
        'late-completion',
        `Late Record${lateCompletionCount === 1 ? '' : 's'}`,
        Types.QuickLinkType.LATE_COMPLETION,
        <>A record is late if it's incomplete<br />and more than 2 weeks old</>);
    }

    return items;
  }, [
    user,
    incompleteCount,
    rejectedCount,
    outdatedCount,
    lateCompletionCount,
    notReviewedCount,
    deploymentTrackMapCount,
    overdueVaccinationCount,
    defaultDatePathParams,
    startDate,
    getActiveDogs,
  ]);


  const quickLinkCount = quickLinkItems?.reduce((p, i) => p + i.count, 0);


  return (
    <Popover
      {...rest}
      id='quick-links-menu'
      label={label(quickLinkCount, quickLinkRange)}
      closeOnClick
    >
      <Box sx={{
        padding: theme => theme.spacing(2),
        '& > section:not(:last-child)': {
          marginBottom: theme => theme.spacing(2),
        },
      }}>

        {/* Quick Links */}
        {quickLinkItems.length > 0
          ? <section>
            <Box sx={{ '& > *:not(:first-of-type)': { marginTop: theme => theme.spacing(1) } }}>
              {quickLinkItems.map(quickLink =>
                <CardLink
                  id={quickLink.id}
                  key={quickLink.id}
                  iconType={quickLink.icon}
                  number={quickLink.count}
                  label={quickLink.title}
                  component={quickLink.component}
                  {...quickLink.componentParams}
                  isActive={activeRecordFilterId === quickLink.id}
                  tooltip={quickLink.tooltip}
                  secondaryLabel={quickLink.secondaryLabel}
                />
              )}
            </Box>
          </section>
          : <>No To Do items</>
        }

      </Box>
    </Popover>
  );
}